/**
 * Subtree classification utility for delete/copy subtree operations.
 *
 * Uses a two-phase approach to correctly handle diamond/DAG patterns:
 * Phase 1: BFS collects all reachable descendants as deletion candidates — O(n + edges)
 * Phase 2: Worklist propagation preserves nodes with surviving parents — O(n + edges)
 *
 * Total complexity: O(n + edges), where n = number of nodes reachable from rootNodeId.
 */

export interface SubtreeClassification {
    /** Node IDs that will be deleted (exclusively owned by the subtree) */
    deletable: string[];
    /** Node IDs that will be preserved (have surviving parents outside the subtree) */
    preserved: string[];
    /** All reachable node IDs from the subtree root (deletable + preserved) */
    all: string[];
}

interface NodeLike {
    id: string;
    children: Array<{ nodeId: string }>;
    deleted?: boolean;
}

/**
 * Classify subtree nodes as deletable or preserved.
 *
 * @param rootNodeId - The root of the subtree to classify
 * @param allNodes - All nodes in the decision tree content
 * @returns Classification with deletable, preserved, and all node ID arrays
 */
export function classifySubtree(rootNodeId: string, allNodes: NodeLike[]): SubtreeClassification {
    const nodeMap = new Map(allNodes.map(n => [n.id, n]));

    // Phase 1: BFS to collect all reachable descendants as deletion candidates
    // Uses index pointer instead of shift() to avoid O(n²)
    const candidates = new Set<string>();
    const allReachable: string[] = [];
    const queue: string[] = [rootNodeId];
    let head = 0;
    while (head < queue.length) {
        const cur = queue[head++];
        if (candidates.has(cur)) continue;
        const node = nodeMap.get(cur);
        if (!node || node.deleted) continue;
        candidates.add(cur);
        allReachable.push(cur);
        for (const c of node.children) queue.push(c.nodeId);
    }

    // Build child → parents index for efficient lookup
    const childToParents = new Map<string, string[]>();
    for (const n of allNodes) {
        if (n.deleted) continue;
        for (const c of n.children) {
            const parents = childToParents.get(c.nodeId) || [];
            parents.push(n.id);
            childToParents.set(c.nodeId, parents);
        }
    }

    // Phase 2: Worklist propagation — seed with initially-preservable nodes,
    // then propagate to their children (a preserved node is a surviving parent for its children).
    // This replaces the O(n²) fixed-point loop with O(n + edges) worklist.
    const worklist: string[] = [];
    for (const id of candidates) {
        if (id === rootNodeId) continue;
        const parents = childToParents.get(id) || [];
        if (parents.some(p => !candidates.has(p))) worklist.push(id);
    }
    while (worklist.length > 0) {
        const id = worklist.pop()!;
        if (!candidates.has(id)) continue;
        candidates.delete(id);
        const node = nodeMap.get(id);
        if (!node) continue;
        // This preserved node is now a surviving parent for its children
        for (const c of node.children) {
            if (candidates.has(c.nodeId) && c.nodeId !== rootNodeId) worklist.push(c.nodeId);
        }
    }

    const deletable = [...candidates];
    const preserved = allReachable.filter(id => !candidates.has(id));

    return { deletable, preserved, all: allReachable };
}
